import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BookOpen, Calendar, Plus } from "lucide-react";
import Layout from "../../components/Layout/Layout";
import LoadingState from "../../components/Common/LoadingState";
import { AuthContext } from "../../context";
import { getDashboardSummary } from "../../api/dashboardApi";
import { useAsyncAction } from "../../hooks/useAsyncAction";

const getFirstName = (user) => {
    if (!user) return null;
    return user.firstName || user.firstname || user.userName || user.username || null;
};

// TEMPORARY — hides the "Order availability" banner on the dashboard.
// Flip back to true to restore it.
const SHOW_ORDER_AVAILABILITY_BANNER = false;

const Dashboard = () => {
    const { t } = useTranslation();
    const { user } = useContext(AuthContext);

    const [summary, setSummary] = useState(null);
    const summaryAction = useAsyncAction();
    const { run: runLoadSummary } = summaryAction;
    const isPageLoading = summaryAction.isLoading && !summary;

    useEffect(() => {
        let mounted = true;

        const load = async () => {
            const summaryResult = await runLoadSummary(
                () => getDashboardSummary(),
                { errorFallback: t("DASHBOARD_ERROR_LOAD_SUMMARY"), silentErrorToast: true }
            );
            if (mounted) {
                setSummary(summaryResult || null);
            }
        };

        load();
        return () => {
            mounted = false;
        };
    }, [runLoadSummary, t]);

    const resolveThumbnail = (url) => {
        if (!url) return "/assets/images/dashboard/D1.png";
        const iframeMatch = url.match(/iframe\.videodelivery\.net\/([a-f0-9]+)/i);
        if (iframeMatch) return `https://videodelivery.net/${iframeMatch[1]}/thumbnails/thumbnail.jpg`;
        return url;
    };

    const recentContent = summary?.recentPosts || [];
    const hasContent = recentContent.length > 0;

    const parseApiDate = (dateStr) => {
        if (!dateStr) return null;
        const [datePart] = dateStr.split(" ");
        const [day, month, year] = datePart.split("-");
        return new Date(year, month - 1, day);
    };

    const computeOrderBanner = () => {
        if (!summary) return null;

        // Not pre-approved — block all orders
        if (summary.isFirstOrderPreApproved === false) return { canOrder: false, nextOrderDate: null };

        // First order — pre-approved, allow immediately
        if ((summary.ordersCount ?? 0) === 0) return { canOrder: true };

        // Subsequent orders — check frequency
        const latestOrderDate = summary.latestOrderDate;
        const frequency = summary.orderFrequency;
        if (!latestOrderDate) return { canOrder: true };
        const parsed = parseApiDate(latestOrderDate);
        if (!parsed || !frequency) return { canOrder: true };
        const next = new Date(parsed);
        next.setDate(next.getDate() + frequency);
        const canOrder = next <= new Date();
        return {
            canOrder,
            nextOrderDate: canOrder ? null : next.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }),
        };
    };

    const orderBanner = computeOrderBanner();

    const stats = [
        {
            value: summary ? `${summary.ordersCount}` : "0",
            label: t("DASHBOARD_STATS_ORDERS_LABEL"),
            cta: t("DASHBOARD_STATS_CTA_ORDERS"),
            to: "/gift-orders",
        },
        {
            value: summary ? `${summary.giftsCount}` : "0",
            label: t("DASHBOARD_STATS_GIFTS_LABEL"),
            cta: t("DASHBOARD_STATS_CTA_GIFTS"),
            to: "/my-gifts",
        },
        {
            value: summary ? `${summary.contentCount}` : "0",
            label: t("DASHBOARD_STATS_CONTENT_LABEL"),
            cta: t("DASHBOARD_STATS_CTA_CONTENT"),
            to: "/my-content",
        },
        {
            value: "",
            label: t("DASHBOARD_STATS_GUIDE_LABEL"),
            cta: t("DASHBOARD_STATS_CTA_GUIDE"),
            icon: "book",
            to: "/help",
        },
    ];

    if (isPageLoading) {
        return (
            <Layout activeLabel="Home">
                <LoadingState message={t("DASHBOARD_LOADING")} fullScreen />
            </Layout>
        );
    }

    return (
        <Layout activeLabel="Home">
            <div>
                <img
                    src="/assets/images/dashboard/top.png"
                    alt=""
                    className="h-40 w-full rounded-xl object-cover sm:h-48 md:h-full md:rounded-none"
                />
                <div className="mt-6">
                    <h1 className="text-lg font-semibold text-primary">{t("DASHBOARD_GREETING")} {getFirstName(user) || t("COMMON_USER")}</h1>
                    <p className="mt-1 text-sm text-slate-500">
                        {t("DASHBOARD_WELCOME_BACK")}
                    </p>
                </div>

                {SHOW_ORDER_AVAILABILITY_BANNER && orderBanner && orderBanner.canOrder && (
                    <Link
                        to="/product-collections"
                        className={`mt-4 flex items-center gap-4 rounded-xl border px-5 py-4 transition hover:shadow-sm ${
                            orderBanner.canOrder
                                ? "border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 hover:border-green-300"
                                : "border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 hover:border-amber-300"
                        }`}
                    >
                        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${orderBanner.canOrder ? "bg-green-100 text-green-600" : "bg-amber-100 text-amber-600"}`}>
                            <Calendar className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                            <p className={`text-xs font-medium uppercase tracking-wide ${orderBanner.canOrder ? "text-green-600" : "text-amber-600"}`}>
                                {t("DASHBOARD_ORDER_AVAILABILITY")}
                            </p>
                            <p className={`mt-0.5 text-sm font-semibold ${orderBanner.canOrder ? "text-green-900" : "text-amber-900"}`}>
                                {orderBanner.canOrder
                                    ? t("DASHBOARD_ORDER_CAN_PLACE_NOW")
                                    : t("DASHBOARD_ORDER_NEXT_SOON")}
                            </p>
                        </div>
                        <div className={`shrink-0 ${orderBanner.canOrder ? "text-green-400" : "text-amber-400"}`}>
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                        </div>
                    </Link>
                )}

                <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                    {stats.map((item) => (
                        <Link
                            key={item.label}
                            to={item.to}
                            className="flex flex-col rounded-md border border-slate-200 bg-white p-4 shadow-[0_10px_20px_rgba(15,23,42,0.06)] transition hover:-translate-y-0.5 hover:shadow-[0_18px_30px_rgba(15,23,42,0.08)]"
                        >
                            <div className="text-2xl font-semibold text-slate-900">
                                {item.value || <BookOpen className="h-8 w-8" />}
                            </div>
                            <p className="mt-2 text-sm text-slate-500">{item.label}</p>
                            <span className="mt-auto pt-3 block text-xs font-semibold text-primary underline underline-offset-4">
                                {item.cta}
                            </span>
                        </Link>
                    ))}
                </div>

                <div className="mt-8">
                    <div className="flex items-center justify-between">
                        <h2 className="text-sm font-semibold text-slate-900">{t("DASHBOARD_RECENT_POSTS")}</h2>
                        {hasContent ? (
                            <Link
                                to="/my-content"
                                className="text-xs font-semibold text-primary underline underline-offset-4"
                            >
                                {t("DASHBOARD_VIEW_ALL_CONTENT")}
                            </Link>
                        ) : null}
                    </div>

                    {hasContent ? (
                        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                            {recentContent.map((item) => (
                                <Link
                                    key={item.id}
                                    to={`/my-content/${item.id}`}
                                    className="group relative block h-72 overflow-hidden rounded-lg border border-slate-200 bg-slate-900 shadow-[0_10px_20px_rgba(15,23,42,0.12)] transition hover:-translate-y-0.5 hover:shadow-[0_18px_30px_rgba(15,23,42,0.18)]"
                                >
                                    <img
                                        src={resolveThumbnail(item.thumbnail)}
                                        alt={item.title || t("DASHBOARD_RECENT_POST_FALLBACK_ALT")}
                                        className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-transparent" />
                                    <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                                        <p className="line-clamp-2 text-sm font-medium leading-snug">
                                            {item.title}
                                        </p>
                                        <p className="mt-2 text-xs text-white/80">{item.date}</p>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    ) : (
                        <Link
                            to="/my-content/new"
                            className="mt-4 block rounded-md border border-slate-200 bg-input-bg px-6 py-12 text-center sm:py-16 transition hover:bg-[#efefef] hover:border-primary/30"
                        >
                            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-primary text-primary">
                                <Plus className="h-5 w-5" />
                            </div>
                            <p className="mt-3 text-sm text-slate-600">
                                {t("DASHBOARD_UPLOAD_FIRST_CONTENT")}
                            </p>
                        </Link>
                    )}

                    <div className="relative mt-6 overflow-hidden rounded-md bg-[#eef0f3]">
                        <img
                            src="/assets/images/dashboard/bottom.png"
                            alt=""
                            className="absolute inset-0 h-full w-full object-cover"
                        />
                        <div className="relative z-10 flex flex-col items-center justify-center px-6 py-10 text-center">
                            <h2 className="text-2xl font-semibold text-slate-900">{t("DASHBOARD_NEED_HELP")}</h2>
                            <p className="mt-3 max-w-md text-sm text-slate-500">
                                {t("DASHBOARD_HELP_SUBTITLE")}
                            </p>
                            <Link
                                to="/help"
                                className="mt-5 text-sm font-semibold text-primary underline underline-offset-4 hover:opacity-80"
                            >
                                {t("DASHBOARD_LEARN_MORE")}
                            </Link>
                        </div>
                    </div>
                </div>


            </div>
        </Layout>
    );
};

export default Dashboard;
